/**
 * PAPPO — NewGroupModal
 * Cria um grupo DE UTILIZADOR: nome + escolha de contatos (checkboxes).
 * Os membros vêm dos CONTATOS do utilizador (para crianças, já mediados pelo
 * responsável — sem aprovação extra). O criador vira admin.
 */

import { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import type { Conversation } from '../../types/message';
import type { Contact } from '../../types/user';
import { listContacts } from '../../services/contacts';
import { createGroup } from '../../services/groups';
import { Avatar } from '../common/Avatar';
import { Button } from '../common/Button';
import styles from './GroupModal.module.css';

interface NewGroupModalProps {
  onClose: () => void;
  /** Chamado com o grupo criado (para o abrir/atualizar a lista). */
  onCreated: (group: Conversation) => void;
}

export function NewGroupModal({ onClose, onCreated }: NewGroupModalProps) {
  const [name, setName] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listContacts().then((res) => {
      if (res.success && res.data) {
        setContacts(
          res.data.contacts.filter(
            (c) => c.status === 'accepted' && c.contact_user?.id
          )
        );
      }
    });
  }, []);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Dê um nome ao grupo.');
      return;
    }
    if (selected.size === 0) {
      setError('Escolha ao menos um contato.');
      return;
    }
    setSaving(true);
    setError('');
    const res = await createGroup(trimmed, Array.from(selected));
    setSaving(false);
    if (res.success && res.data?.group) {
      onCreated(res.data.group);
      onClose();
    } else {
      setError(res.error || 'Não foi possível criar o grupo.');
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <span className={styles.title}>Novo grupo</span>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <label className={styles.label} htmlFor="group-name">
            Nome do grupo
          </label>
          <input
            id="group-name"
            className={styles.input}
            value={name}
            maxLength={120}
            placeholder="Ex.: Primos, Turma, Vizinhança"
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          <div className={styles.sectionLabel}>
            <span>Adicionar contatos</span>
            <span>{selected.size} selecionado(s)</span>
          </div>

          {contacts.length === 0 ? (
            <p className={styles.empty}>
              Você ainda não tem contatos para adicionar.
            </p>
          ) : (
            <ul className={styles.list}>
              {contacts.map((c) => {
                const u = c.contact_user!;
                const on = selected.has(u.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={styles.row}
                      onClick={() => toggle(u.id)}
                      aria-pressed={on}
                    >
                      <Avatar name={u.display_name} src={u.avatar_url} size="sm" />
                      <div className={styles.rowInfo}>
                        <span className={styles.rowName}>{u.display_name}</span>
                        <span className={styles.rowMeta}>
                          {u.role === 'child' ? 'Menor' : 'Responsável'}
                        </span>
                      </div>
                      <span className={`${styles.check} ${on ? styles.checkOn : ''}`}>
                        {on && <Check size={14} />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {error && <p className={styles.error}>{error}</p>}
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} fullWidth>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            isLoading={saving}
            fullWidth
          >
            Criar grupo
          </Button>
        </div>
      </div>
    </div>
  );
}
